import { DISCORD_URL } from 'features/pages/slice';
import { Bubble, Card, Icon, Loader } from '@frontendmentorio/design-system-v2';

const REPO_TYPES = ['CSS', 'JavaScript'];

const NO_ISSUES_MESSAGES = {
  accessibility:
    "Great job! Our automated checks found no accessibility issues, but we recommend manually testing your project as automated tests can't catch everything.",
  css: "Great job! Our automated checks found no styling issues, but we recommend manually testing your project as automated tests can't catch everything.",
  html: 'Great job! Our automated checks didn’t detect any HTML issues in this project.',
  javascript:
    "Great job! Our automated checks found no JavaScript issues, but we recommend manually testing your project to ensure it works as expected as automated tests can't catch everything.",
};

export default function EmptyState({ report, solution, status, type }) {
  const isRepoType = REPO_TYPES.includes(type);
  const isGitHub = solution.repoURL.includes('github.com');
  const isMonoRepo = /github\.com\/[^/]+\/[^/]+\/.+$/.test(solution.repoURL);

  if (report?.fileCount === 0) {
    return (
      <Card className="flex gap-4 items-start p-4">
        <Bubble ariaHidden className="bg-rose-300">
          <Icon className="text-xl text-neutral-900" name="file" />
        </Bubble>

        <div className="flex flex-col gap-3">
          <h2 className="text-xl font-secondary">No {type} files detected</h2>

          <p className="text-neutral-500">
            We didn’t find any {type} files in this project, so there’s nothing
            to analyze in this report.
          </p>
        </div>
      </Card>
    );
  }

  if (status === 'failed') {
    return (
      <Card className="flex gap-4 items-start p-4">
        <Bubble ariaHidden className="bg-rose-300">
          <Icon
            className="text-xl text-neutral-900"
            name="triangle-exclamation"
            style="solid"
          />
        </Bubble>
        <div className="flex flex-col gap-3 w-full">
          <h2 className="text-xl font-secondary">
            Unfortunately, we were unable to generate reports for this project
          </h2>

          <div className="flex flex-col gap-2">
            <p className="font-semibold">{report.error}</p>

            <p className="text-neutral-500">
              You can try to generate a new report by clicking the button above.
              If the problem persists, please reach out on{' '}
              <a
                className="underline hover:no-underline"
                href={DISCORD_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                Discord
              </a>
              , or email us at{' '}
              <a
                className="underline hover:no-underline"
                href="mailto:hi@frontendmentor.io"
              >
                hi@frontendmentor.io
              </a>
            </p>
          </div>
        </div>
      </Card>
    );
  }

  if (report?.error) {
    return (
      <Card className="flex gap-4 items-start p-4">
        <Bubble ariaHidden className="bg-rose-300">
          <Icon
            className="text-xl text-neutral-900"
            name="triangle-exclamation"
            style="solid"
          />
        </Bubble>

        <div className="flex flex-col gap-3 w-full">
          <h2 className="text-xl font-secondary">
            Unfortunately, we were unable to generate a {type} report for this
            project
          </h2>

          <div className="flex flex-col gap-2">
            <p className="font-semibold">{report.error}</p>

            {isRepoType && !isGitHub && (
              <p className="text-neutral-500">
                We can only fetch files from GitHub repositories right now.
                Please check your URL or upload your project to GitHub to
                continue.
              </p>
            )}

            {isRepoType && isMonoRepo && (
              <p className="text-neutral-500">
                It looks like this project is part of a monorepo. We can’t fetch
                files for individual projects in monorepos. Please move this
                project to its own repo and try again.
              </p>
            )}

            <p className="text-neutral-500">
              You can try to generate a new report by clicking the button above.
              If the problem persists, please reach out on{' '}
              <a
                className="underline hover:no-underline"
                href={DISCORD_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                Discord
              </a>
              , or email us at{' '}
              <a
                className="underline hover:no-underline"
                href="mailto:hi@frontendmentor.io"
              >
                hi@frontendmentor.io
              </a>
            </p>
          </div>
        </div>
      </Card>
    );
  }

  if (status === 'processing') {
    return (
      <Card className="flex gap-4 items-start p-4">
        <Bubble ariaHidden className="bg-neutral-200">
          <Loader className="gap-[0.4rem] *:bg-neutral-900 *:w-[0.5rem]" />
        </Bubble>

        <div className="flex flex-col gap-3">
          <h2 className="text-xl font-secondary">
            We are currently processing your reports
          </h2>

          <div className="flex flex-col gap-2 text-neutral-500">
            <p>
              This usually takes less than 5 minutes, but can be longer during
              busy periods.
            </p>

            <p>
              This page will refresh automatically when your reports are ready.
            </p>
          </div>
        </div>
      </Card>
    );
  }

  if (!report) {
    return (
      <Card className="flex gap-4 items-start p-4">
        <Bubble ariaHidden className="bg-rose-300">
          <Icon
            className="text-xl text-neutral-900"
            name="triangle-exclamation"
            style="solid"
          />
        </Bubble>

        <div className="flex flex-col gap-3">
          <h2 className="text-xl font-secondary">
            We could not find a report for this project
          </h2>

          <p className="text-neutral-500">
            You can generate a new one using the button above.
          </p>
        </div>
      </Card>
    );
  }

  return (
    <Card className="flex gap-4 items-center p-4">
      <Bubble ariaHidden className="bg-emerald-400">
        <Icon className="text-xl text-neutral-900" name="check" style="solid" />
      </Bubble>

      <div className="flex flex-col gap-3">
        <h2 className="text-xl font-secondary">No {type} issues found</h2>

        <p className="text-neutral-500">
          {NO_ISSUES_MESSAGES[type.toLowerCase()]}
        </p>
      </div>
    </Card>
  );
}
